import { get, isEqual, setWith, toPath } from 'lodash';
import { ReactNode } from 'react';

import {
    ValidationErrorData,
    ValidationErrorsData,
    ValidationResultItem
} from '../../typings/common';
import {
    GdAddSmartFilterConditionInput,
    GdSmartFilterTab,
    GdUpdateSmartFilterConditionInput
} from '../../typings/graphql-grid';
import { FeedFilterCondition } from '../../typings/groupsEditState';

import { filtersTextErrorMapper } from './GroupFiltersErrorsTextMapper/GroupFiltersErrorsTextMapper';

export const filterFieldErrorMapper = function(
    index: string
): Array<{ serverPath: string; clientPath: string }> {
    return [
        {
            serverPath: `name`,
            clientPath: `name`
        },
        {
            serverPath: `priceCpc`,
            clientPath: `priceCpc`
        },
        {
            serverPath: `priceCpa`,
            clientPath: `priceCpa`
        },
        {
            serverPath: `conditions.${index}.stringValue`,
            clientPath: `conditions.${index}`
        },
        {
            serverPath: `conditions`,
            clientPath: `allConditions`
        },
        {
            serverPath: `addItems.${index}`,
            clientPath: `filterItself`
        },
        {
            serverPath: `updateItems.${index}`,
            clientPath: `filterItself`
        }
    ];
};

export const mapValidationResultItemsToFieldsFilter = (
    errors: Array<ValidationResultItem>,
    tab: GdSmartFilterTab = GdSmartFilterTab.ALL_PRODUCTS
): ValidationErrorsData => {
    return errors.reduce((acc, error) => {
        const clientPath = findGroupEditErrorClientPath(error.path);
        const fieldErrors = get(acc, clientPath);

        const errorObj: { code: string; text: ReactNode } = {
            code: error.code,
            text: filtersTextErrorMapper(
                {
                    code: error.code,
                    path: clientPath.join('.'),
                    params: error.params
                },
                {
                    isTreeTab: tab === GdSmartFilterTab.TREE
                }
            )
        };
        if (!fieldErrors) {
            setWith(acc, clientPath, [errorObj], Object);
        } else {
            fieldErrors.push(errorObj);
        }
        return acc;
    }, {});
};

export const findGroupEditErrorClientPath = (
    errorPath: string,
    placeHolder: string = '_INDEX_'
): Array<string> => {
    const errorPathFullArray = toPath(errorPath).slice();
    const errorPathArray =
        errorPathFullArray.length === 2
            ? errorPathFullArray
            : errorPathFullArray.slice(2);
    const mapper = filterFieldErrorMapper(placeHolder);

    let index = '';

    const serverPathWithoutIndexes = errorPathArray.map((x: string) => {
        if (isFinite(Number(x)) && !index) {
            index = x;
            return placeHolder;
        }
        return x;
    });

    const clientPathArr = mapper.find(mapObg => {
        const errorObjPathArr = toPath(mapObg.serverPath);

        return isEqual(
            serverPathWithoutIndexes.slice(0, errorObjPathArr.length),
            errorObjPathArr
        );
    });
    let result: string[];

    if (clientPathArr) {
        if (index) {
            result = toPath(
                clientPathArr.clientPath.replace(placeHolder, index)
            );
        } else {
            result = toPath(clientPathArr.clientPath);
        }
    } else {
        result = [];
    }

    result.push('errors');

    return result;
};

export const defineTreeTabValidationErrors = (
    validationErrors: ValidationErrorsData,
    conditions: Array<
        GdUpdateSmartFilterConditionInput | GdAddSmartFilterConditionInput
    >,
    tab: GdSmartFilterTab
): ValidationErrorsData => {
    const CONDITION_TO_FIELD_MAP = {
        vendor: 'vendors',
        price: 'priceRange',
        categoryId: 'categoryIds'
    };

    if (!validationErrors.conditions || tab !== GdSmartFilterTab.TREE) {
        return validationErrors;
    }

    Object.keys(validationErrors.conditions).map(invalidConditionIndex => {
        const invalidCondition = conditions[invalidConditionIndex];

        Object.keys(CONDITION_TO_FIELD_MAP).forEach(conditionField => {
            if (invalidCondition.field === conditionField) {
                const fieldName = CONDITION_TO_FIELD_MAP[conditionField];

                validationErrors[fieldName] =
                    validationErrors.conditions[invalidConditionIndex];

                delete validationErrors.conditions[invalidConditionIndex];
            }
        });
    });

    return validationErrors;
};

export const getConditionsErrorsIndexes = (
    conditions: Array<FeedFilterCondition>
): Array<number> => {
    const initialIndexes: Array<number> = [];

    return conditions.reduce((indexes, condition, index) => {
        if (condition.errors && condition.errors.length !== 0) {
            return indexes.concat(index + 1);
        }
        return indexes;
    }, initialIndexes);
};

export const getRateFormErrors = (
    errors: ValidationErrorsData
): Array<ValidationErrorData> => {
    const rateErrors = errors.priceCpc || errors.priceCpa;

    return rateErrors && rateErrors.errors ? [rateErrors.errors[0]] : [];
};
